import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [attempts, setAttempts] = useState(0);
  const { refreshEntitlement } = useAuth();

  // Subscription just started → re-check the account's Wrapulator tier immediately.
  useEffect(() => { if (status === 'paid') refreshEntitlement(true); }, [status, refreshEntitlement]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let a = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.status === 'expired' || data.status === 'failed') { setStatus('failed'); return; }
        a += 1; setAttempts(a);
        if (a > 20) { setStatus('timeout'); return; }
        setTimeout(poll, 1500);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="mi-surface w-full max-w-lg p-10 text-center">
        {status === 'paid' && (
          <>
            <CheckCircle className="w-14 h-14 text-[#E63946] mx-auto mb-6" />
            <h1 className="mi-headline text-4xl mb-3">Trial started</h1>
            <p className="text-[#A0AAB5] mb-8">Your 7-day free trial is active. You won't be charged until it ends — cancel anytime from your Stripe account.</p>
            <Link to="/" data-testid="success-return-home" className="mi-btn-red">
              <ArrowLeft className="w-4 h-4" /> Back to Motor iNKS
            </Link>
          </>
        )}
        {status === 'polling' && (
          <>
            <div className="mi-headline text-2xl mb-4">Confirming payment…</div>
            <p className="text-[#A0AAB5] text-sm">Attempt {attempts} — this usually takes 2-5 seconds.</p>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="mi-headline text-3xl mb-3 text-[#E63946]">Payment failed</div>
            <p className="text-[#A0AAB5] mb-6">Please try again or contact sales@motorinks.com.</p>
            <Link to="/" className="mi-btn-ghost">Return home</Link>
          </>
        )}
        {status === 'timeout' && (
          <>
            <div className="mi-headline text-2xl mb-3">Still processing…</div>
            <p className="text-[#A0AAB5] mb-6">Payment is taking longer than usual. Check your email or refresh.</p>
            <Link to="/" className="mi-btn-ghost">Return home</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="mi-headline text-2xl mb-3">Something went wrong</div>
            <Link to="/" className="mi-btn-ghost">Return home</Link>
          </>
        )}
      </div>
    </div>
  );
}
